import { defineStore } from 'pinia'
import type { CacheKeys as AppCacheKeys } from '@/enum/cacheKeys.ts'

type CacheKeys = AppCacheKeys | string

type CacheValue = string | number | boolean | Record<string, unknown>

type Cache = {
  expire?: string
  value: CacheValue
  flash?: boolean
}

const isStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__'
    localStorage.setItem(test, 'test')
    localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

export const useCacheStore = defineStore('cache', () => {
  const cache = reactive<Partial<Record<CacheKeys, Cache>>>({})

  const isExpired = (flag: Cache): boolean => {
    if (!flag.expire) return false
    return new Date() > new Date(flag.expire)
  }

  const get = (key: CacheKeys): Cache | undefined => {
    const flag = cache[key]
    if (!flag) return undefined

    if (isExpired(flag)) {
      remove(key)
      return undefined
    }

    if (flag.flash) {
      remove(key)
    }

    return flag
  }

  const set = <T extends CacheValue>(
    key: CacheKeys,
    value: T,
    options: { expire?: string, flash?: boolean } = { flash: false }, // Fixed expire type
  ): Cache => {
    return cache[key] = { expire: options.expire, value, flash: options.flash }
  }

  const setOrGet = (key: CacheKeys, value: CacheValue, options: { expire?: string, flash?: boolean } = {}) => {
    const existingCache = get(key) // Fixed variable name
    if (existingCache) {
      return existingCache
    }

    return set(key, value, options)
  }

  const remove = (key: CacheKeys) => {
    delete cache[key]
  }

  const load = () => {
    if (!isStorageAvailable()) return // Check storage availability first

    try {
      const storeId = useCacheStore().$id
      const data = localStorage.getItem(storeId)
      if (data) {
        const parsed = JSON.parse(data)
        if (typeof parsed === 'object' && parsed !== null) {
          Object.assign(cache, parsed)
          cleanupExpired()
        }
      }
    } catch (e) {
      console.error('Failed to load cache from storage:', e)
      try {
        const storeId = useCacheStore().$id
        localStorage.removeItem(storeId)
      } catch {
        // Ignore cleanup errors
      }
    }
  }

  const clearAll = () => {
    Object.keys(cache).forEach(key => delete cache[key])
    // Also clear from localStorage
    if (isStorageAvailable()) {
      try {
        const storeId = useCacheStore().$id
        localStorage.removeItem(storeId)
      } catch (e) {
        console.error('Failed to clear cache from storage:', e)
      }
    }
  }

  const cleanupExpired = () => {
    Object.keys(cache).forEach(key => {
      const flag = cache[key]
      if (flag && isExpired(flag)) {
        delete cache[key]
      }
    })
  }

  const has = (key: CacheKeys): boolean => {
    const flag = cache[key]
    if (!flag) return false

    if (isExpired(flag)) {
      remove(key)
      return false
    }

    return true
  }

  const getValue = <T extends CacheValue>(key: CacheKeys): T | undefined => {
    const flag = get(key)
    return flag?.value as T | undefined
  }

  const setWithTTL = (key: CacheKeys, value: CacheValue, ttlSeconds: number) => { // Simplified parameter type
    const expireDate = new Date(Date.now() + ttlSeconds * 1000)
    return set(key, value, { expire: expireDate.toISOString() })
  }

  load()

  let storeInstance: ReturnType<typeof useCacheStore> | null = null

  const setupSubscription = () => {
    if (!storeInstance) {
      storeInstance = useCacheStore()
    }

    storeInstance.$subscribe(
      (mutation, state) => {
        if (!isStorageAvailable()) return
        if (!storeInstance) return

        try {
          localStorage.setItem(storeInstance.$id, JSON.stringify(state.cache))
        } catch (e) {
          console.error('Failed to save cache to storage:', e)
        }
      },
      { detached: true } // Prevent memory leaks
    )
  }

  nextTick(() => {
    setupSubscription()
  })

  return {
    cache: readonly(cache),
    get,
    set,
    setOrGet,
    remove,
    clearAll,
    cleanupExpired,
    has,
    getValue,
    setWithTTL,
  }
})
